import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCwIcon, SettingsIcon, TrashIcon } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { apiFetch } from '@/utils/api';

interface DashboardWidgetProps {
    widget: {
        id: string;
        title: string;
        description?: string;
        metric_id: string;
        visualization_type: 'table' | 'bar' | 'line';
        show_legend: boolean;
        show_grid: boolean;
        color_scheme?: string;
        dimensions?: string[];
        sort_by?: string;
    };
    onEdit?: (widgetId: string) => void;
    onDelete?: (widgetId: string) => void;
    onRefresh?: (widgetId: string) => void;
}

interface WidgetData {
    widget_id: string;
    metric_name: string;
    metric_code: string;
    value_type: string;
    decimal_places: number;
    visualization_type: string;
    results: Array<{
        dimensions: Record<string, any>;
        value: number;
    }>;
    execution_time_ms: number;
}

export function DashboardWidget({ widget, onEdit, onDelete, onRefresh }: DashboardWidgetProps) {
    const { t } = useI18n();
    const [data, setData] = useState<WidgetData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiFetch(`/api/dashboard/widgets/${widget.id}/data`, {
                method: 'POST',
            });

            if (response.ok) {
                const widgetData = await response.json();
                setData(widgetData);
            } else {
                throw new Error('Failed to fetch widget data');
            }
        } catch (err) {
            console.error('Error fetching widget data:', err);
            setError(t('dashboard.failedToLoad', 'common'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [widget.id]);

    const formatValue = (value: number, valueType: string, decimalPlaces: number) => {
        if (valueType === 'currency') {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'HUF',
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            }).format(value);
        } else if (valueType === 'percentage') {
            return `${value.toFixed(decimalPlaces)}%`;
        } else if (valueType === 'integer') {
            return Math.round(value).toLocaleString();
        } else {
            return value.toFixed(decimalPlaces).toLocaleString();
        }
    };

    const handleRefresh = () => {
        fetchData();
        if (onRefresh) {
            onRefresh(widget.id);
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <RefreshCwIcon className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            );
        }

        if (error || !data) {
            return (
                <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <p className="text-muted-foreground">{error || t('dashboard.noDataAvailable', 'common')}</p>
                    <Button onClick={handleRefresh} variant="outline" size="sm">
                        <RefreshCwIcon className="h-4 w-4 mr-2" />
                        {t('actions.retry', 'common')}
                    </Button>
                </div>
            );
        }

        // Prepare chart data - using same logic as metrics execute page
        const dimensionKeys = data.results.length > 0
            ? Object.keys(data.results[0].dimensions)
            : [];

        // Helper to check if a dimension is month-like
        const isMonthDimension = (code: string) => code === 'month' || code.endsWith('_month');

        // Helper to format month values
        const toYYYYMM = (value: any): string => {
            if (value == null) return '';
            if (typeof value === 'string') {
                const m = value.match(/^(\d{4})-(\d{2})/);
                if (m) return `${m[1]}-${m[2]}`;
            }
            const d = new Date(value);
            if (!isNaN(d.getTime())) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                return `${y}-${m}`;
            }
            if (typeof value === 'object' && value && 'year' in value && 'month' in value) {
                const y = (value as any).year;
                const m = String((value as any).month).padStart(2, '0');
                return `${y}-${m}`;
            }
            return String(value ?? '');
        };

        let chartData: any[] = [];
        let seriesKeys: string[] = [];

        if (dimensionKeys.length === 0) {
            // No dimensions - single value
            chartData = [{ name: 'Value', value: data.results[0]?.value || 0 }];
        } else if (dimensionKeys.length === 1) {
            // Single dimension - simple chart
            const onlyDim = dimensionKeys[0];
            chartData = data.results.map((result) => {
                const raw = result.dimensions[onlyDim];
                const name = isMonthDimension(onlyDim) ? toYYYYMM(raw) : (raw || 'Value');
                return { name, value: result.value };
            });
        } else {
            // Multiple dimensions - stacked chart
            // Use sort_by dimension as X-axis, stack by other dimensions
            const sortDimension = widget.sort_by || widget.dimensions?.[0] || dimensionKeys[0];
            const stackDimension = dimensionKeys.find(k => k !== sortDimension) || dimensionKeys[1];

            // Group data by sort dimension (X-axis)
            const grouped = new Map<string, Map<string, number>>();
            const stackValues = new Set<string>();

            data.results.forEach((result) => {
                const xRaw = result.dimensions[sortDimension] ?? 'Unknown';
                const sRaw = result.dimensions[stackDimension] ?? 'Unknown';
                const xValue = isMonthDimension(sortDimension) ? toYYYYMM(xRaw) : String(xRaw);
                const stackValue = isMonthDimension(stackDimension) ? toYYYYMM(sRaw) : String(sRaw);

                if (!grouped.has(xValue)) grouped.set(xValue, new Map());
                grouped.get(xValue)!.set(stackValue, result.value);
                stackValues.add(stackValue);
            });

            // Convert to chart format
            chartData = Array.from(grouped.entries()).map(([xValue, stacks]) => {
                const dataPoint: any = { name: xValue };
                stacks.forEach((value, stackKey) => {
                    dataPoint[stackKey] = value;
                });
                return dataPoint;
            });

            seriesKeys = Array.from(stackValues);
        }

        if (widget.visualization_type === 'table') {
            // Get dimension keys
            const dimensionKeys = data.results.length > 0
                ? Object.keys(data.results[0].dimensions)
                : [];

            return (
                <div className="h-full overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {dimensionKeys.map((key) => (
                                    <TableHead key={key} className="capitalize">
                                        {key.replace(/_/g, ' ')}
                                    </TableHead>
                                ))}
                                <TableHead className="text-right">{data.metric_name}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.results.map((result, idx) => (
                                <TableRow key={idx}>
                                    {dimensionKeys.map((key) => {
                                        const rawValue = result.dimensions[key];
                                        const displayValue = isMonthDimension(key) ? toYYYYMM(rawValue) : rawValue;
                                        return <TableCell key={key}>{displayValue}</TableCell>;
                                    })}
                                    <TableCell className="text-right font-medium">
                                        {formatValue(result.value, data.value_type, data.decimal_places)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            );
        }

        if (widget.visualization_type === 'bar') {
            // Color palette for multiple series
            const colors = [
                '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
                '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
            ];

            return (
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            {widget.show_grid && <CartesianGrid strokeDasharray="3 3" />}
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                                formatter={(value) => formatValue(Number(value), data.value_type, data.decimal_places)}
                            />
                            {widget.show_legend && <Legend />}
                            {seriesKeys.length > 0 ? (
                                // Multiple dimensions - stacked bars
                                seriesKeys.map((key, idx) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        stackId="a"
                                        fill={colors[idx % colors.length]}
                                        name={key}
                                        animationDuration={200}
                                    />
                                ))
                            ) : (
                                // Single dimension - single bar
                                <Bar dataKey="value" fill="#3b82f6" name={data.metric_name} animationDuration={200} />
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.visualization_type === 'line') {
            // Color palette for multiple series
            const colors = [
                '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
                '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
            ];

            return (
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                            {widget.show_grid && <CartesianGrid strokeDasharray="3 3" />}
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                                formatter={(value) => formatValue(Number(value), data.value_type, data.decimal_places)}
                            />
                            {widget.show_legend && <Legend />}
                            {seriesKeys.length > 0 ? (
                                // Multiple dimensions - multiple lines
                                seriesKeys.map((key, idx) => (
                                    <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={colors[idx % colors.length]}
                                        strokeWidth={2}
                                        name={key}
                                    />
                                ))
                            ) : (
                                // Single dimension - single line
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#3b82f6"
                                    strokeWidth={2}
                                    name={data.metric_name}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        return null;
    };

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex-1 drag-handle cursor-move">
                    <CardTitle className="text-base font-semibold">{widget.title}</CardTitle>
                    {widget.description && (
                        <CardDescription className="text-sm">{widget.description}</CardDescription>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={loading}
                        className="cursor-pointer"
                    >
                        <RefreshCwIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    {onEdit && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(widget.id)}
                            className="cursor-pointer"
                        >
                            <SettingsIcon className="h-4 w-4" />
                        </Button>
                    )}
                    {onDelete && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(widget.id)}
                            className="cursor-pointer"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden">
                {renderContent()}
            </CardContent>
        </Card>
    );
}
